import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectActiveHeroActionId, selectHeroActionTriggerPinId, selectIsCompactViewport, selectSidebarOpen, selectSidebarView, setActiveHeroActionId, setHeroActionTriggerPinId, setSidebarOpen, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { selectExternalMapId } from '@repo/store/maps/slices/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { useEffect, useRef } from 'react';
import { HeroActionDescription } from './hero-action-description';
import { getHeroActionIcon } from './hero-action-icons';
import { useHeroActionForPin } from './hooks/useHeroActionForPin';
const topOffset = 24;
const compactTopOffset = 28;
const desktopDrawerStyle = css({
  backgroundColor: 'blackAlpha.50',
  backdropFilter: '[blur(25px)]',
  position: 'absolute',
  left: '0',
  right: '0',
  bottom: `[calc(100% - ${topOffset}px)]`,
  zIndex: 3,
  borderRadius: '32',
  borderBottomLeftRadius: '[0px]',
  borderBottomRightRadius: '[0px]',
  _compact: {
    borderRadius: '24',
    borderBottomLeftRadius: '[0px]',
    borderBottomRightRadius: '[0px]',
    bottom: `[calc(100% - ${compactTopOffset}px)]`
  },
  overflow: 'hidden',
  boxShadow: 'lg',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: '0',
  smDown: {
    width: 'full',
    maxWidth: '[calc(100vw - 32px)]',
    marginX: '12'
  }
});

// Mobile pane can't host the absolutely positioned peek-above-card drawer because the
// bottom sheet clips anything outside its visible box. Render inline instead.
// Rounded top corners + flat bottom so the drawer slots cleanly into the pin card below.
const mobilePaneDrawerStyle = css({
  backgroundColor: 'blackAlpha.50',
  backdropFilter: '[blur(25px)]',
  position: 'relative',
  width: 'full',
  borderTopLeftRadius: '24',
  borderTopRightRadius: '24',
  borderBottomLeftRadius: '[0px]',
  borderBottomRightRadius: '[0px]',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
});
const mobilePaneBottomSectionStyle = css({
  position: 'relative',
  width: 'full',
  flexShrink: '0',
  borderTop: '[1px solid]',
  borderColor: 'whiteAlpha.10',
  display: 'flex',
  flexDirection: 'column'
});
type HeroActionDrawerProps = {
  selectedPinId: string | undefined;
  nearbyPins?: MapDataLocationFeature[];
  onPinSelect?: (id: string) => void;
  isMobilePane?: boolean;
  /**
   * When true, suppress the nearby-pins nav row even when nearby pins are present.
   * Mobile pane uses this because it has its own nearby-pins section at the bottom.
   */
  heroOnly?: boolean;
  bottomPlacement?: boolean;
};
export const HeroActionDrawer = t0 => {
  const $ = _c(58);
  const {
    selectedPinId,
    nearbyPins: t1,
    onPinSelect,
    isMobilePane: t2,
    heroOnly: t3,
    bottomPlacement: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const nearbyPins = t5;
  const isMobilePane = t2 === undefined ? false : t2;
  const heroOnly = t3 === undefined ? false : t3;
  const bottomPlacement = t4 === undefined ? false : t4;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["game_map"];
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const {
    t
  } = useT(t6);
  const dispatch = useAppDispatch();
  const isCompactViewport = useAppSelector(selectIsCompactViewport);
  const analytics = useAnalytics();
  const hostType = useHostType();
  const mapId = useAppSelector(selectExternalMapId) ?? undefined;
  const sidebarOpen = useAppSelector(selectSidebarOpen);
  const sidebarView = useAppSelector(selectSidebarView);
  const activeHeroActionId = useAppSelector(selectActiveHeroActionId);
  const heroActionTriggerPinId = useAppSelector(selectHeroActionTriggerPinId);
  const horizontalPaddingPx = isMobilePane ? 16 : isCompactViewport ? 12 : 20;
  const heroAction = useHeroActionForPin(selectedPinId);
  const isActiveHeroActionSidebar = sidebarOpen && sidebarView === "heroAction" && heroAction?.externalId === activeHeroActionId && selectedPinId === heroActionTriggerPinId;
  const canOpenHeroAction = !!heroAction && !isActiveHeroActionSidebar;
  const showNearby = !heroOnly && nearbyPins.length > 1 && !!onPinSelect;
  let t7;
  if ($[3] !== nearbyPins || $[4] !== selectedPinId || $[5] !== showNearby) {
    t7 = showNearby ? nearbyPins.findIndex(pin => pin.properties.externalId === selectedPinId) : -1;
    $[3] = nearbyPins;
    $[4] = selectedPinId;
    $[5] = showNearby;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const currentIndex = t7;
  const total = nearbyPins.length;
  const nearbyValid = showNearby && currentIndex >= 0;
  const lastImpressionRef = useRef(null);
  let t8;
  let t9;
  if ($[7] !== analytics || $[8] !== heroAction || $[9] !== hostType || $[10] !== mapId || $[11] !== selectedPinId) {
    t8 = () => {
      if (!heroAction || !selectedPinId) {
        lastImpressionRef.current = null;
        return;
      }
      const key = `${heroAction.externalId}:${selectedPinId}`;
      if (lastImpressionRef.current === key) {
        return;
      }
      lastImpressionRef.current = key;
      analytics.event("game_map_hero_action_impression", {
        mapId,
        heroActionId: heroAction.externalId,
        heroActionName: heroAction.name,
        iconCategory: heroAction.iconCategory,
        priority: heroAction.priority,
        pinId: selectedPinId,
        platform: hostType
      });
    };
    t9 = [heroAction, selectedPinId, analytics, mapId, hostType];
    $[7] = analytics;
    $[8] = heroAction;
    $[9] = hostType;
    $[10] = mapId;
    $[11] = selectedPinId;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  if ($[14] !== analytics || $[15] !== dispatch || $[16] !== heroAction || $[17] !== hostType || $[18] !== mapId || $[19] !== selectedPinId) {
    t10 = () => {
      if (!heroAction || !selectedPinId) {
        return;
      }
      analytics.event("game_map_hero_action_open", {
        mapId,
        heroActionId: heroAction.externalId,
        heroActionName: heroAction.name,
        iconCategory: heroAction.iconCategory,
        priority: heroAction.priority,
        pinId: selectedPinId,
        platform: hostType
      });
      dispatch(setActiveHeroActionId(heroAction.externalId));
      dispatch(setHeroActionTriggerPinId(selectedPinId));
      dispatch(setSidebarView("heroAction"));
      dispatch(setSidebarOpen(true));
    };
    $[14] = analytics;
    $[15] = dispatch;
    $[16] = heroAction;
    $[17] = hostType;
    $[18] = mapId;
    $[19] = selectedPinId;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const handleOpen = t10;
  let t11;
  if ($[21] !== analytics || $[22] !== currentIndex || $[23] !== hostType || $[24] !== mapId || $[25] !== nearbyPins || $[26] !== nearbyValid || $[27] !== onPinSelect || $[28] !== total) {
    t11 = direction => {
      if (!nearbyValid || !onPinSelect) {
        return;
      }
      const offset = direction === "previous" ? -1 : 1;
      const nextId = nearbyPins[(currentIndex + offset + total) % total]?.properties.externalId;
      if (!nextId) {
        return;
      }
      onPinSelect(nextId);
      analytics.event("game_map_nearby_pins_navigate", {
        mapId: mapId ?? "",
        direction,
        platform: hostType
      });
    };
    $[21] = analytics;
    $[22] = currentIndex;
    $[23] = hostType;
    $[24] = mapId;
    $[25] = nearbyPins;
    $[26] = nearbyValid;
    $[27] = onPinSelect;
    $[28] = total;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const navigateNearby = t11;
  if (!heroAction && !nearbyValid) {
    return null;
  }
  let t12;
  if ($[30] !== canOpenHeroAction || $[31] !== handleOpen || $[32] !== heroAction || $[33] !== isMobilePane || $[34] !== t) {
    t12 = heroAction && <FlexRow align="center" justify="between" gap="12" className={css.raw({
      width: "full",
      minWidth: "[0]",
      paddingTop: isMobilePane ? "[0px]" : "[14px]",
      _compact: {
        paddingTop: isMobilePane ? "[0px]" : "12"
      },
      cursor: canOpenHeroAction ? "pointer" : "default"
    })} onClick={canOpenHeroAction ? handleOpen : undefined}><FlexRow align="center" gap="12" className={css.raw({
        flex: "1",
        minWidth: "[0]"
      })}><Icon fill={true} icon={getHeroActionIcon(heroAction.iconCategory)} size={isMobilePane ? "sm" : "lg"} className={css({
          color: "brand.primary",
          flexShrink: "0",
          ...(!isMobilePane && {
            _compact: {
              display: "none"
            }
          })
        })} decorative={true} /><FlexColumn className={css.raw({
          flex: "1",
          minWidth: "[0]"
        })}><Type variant="header-xs" color="white" className={css({
            width: "full",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          })}>{heroAction.name}</Type><HeroActionDescription description={heroAction.description} heroActionId={heroAction.externalId} heroActionName={heroAction.name} variant="smartAction" /></FlexColumn></FlexRow>{canOpenHeroAction && <IconButton onClick={e => {
        e.stopPropagation();
        handleOpen();
      }} color="secondary" size="sm" aria-label={t(_temp, {
        name: heroAction.name
      })} className={css({
        flexShrink: "0"
      })}><Icon icon="arrow_forward" decorative={true} /></IconButton>}</FlexRow>;
    $[30] = canOpenHeroAction;
    $[31] = handleOpen;
    $[32] = heroAction;
    $[33] = isMobilePane;
    $[34] = t;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const heroRow = t12;
  let t13;
  if ($[36] !== currentIndex || $[37] !== heroAction || $[38] !== navigateNearby || $[39] !== nearbyValid || $[40] !== t || $[41] !== total) {
    t13 = nearbyValid && <FlexRow align="center" justify="between" gap="8" className={css.raw({
      width: "full",
      paddingTop: heroAction ? "12" : "[14px]",
      _compact: {
        paddingTop: heroAction ? "12" : "12"
      }
    })}><IconButton onClick={() => navigateNearby("previous")} color="secondary" size="sm" aria-label={t(_temp2)}><Icon icon="arrow_back" decorative={true} /></IconButton><Type variant="caption" color="white">{currentIndex + 1}/{total} {t(_temp3)}</Type><IconButton onClick={() => navigateNearby("next")} color="secondary" size="sm" aria-label={t(_temp4)}><Icon icon="arrow_forward" decorative={true} /></IconButton></FlexRow>;
    $[36] = currentIndex;
    $[37] = heroAction;
    $[38] = navigateNearby;
    $[39] = nearbyValid;
    $[40] = t;
    $[41] = total;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const nearbyRow = t13;
  let t14;
  if ($[43] !== heroAction || $[44] !== horizontalPaddingPx || $[45] !== nearbyValid) {
    t14 = heroAction && nearbyValid && <div className={css({
      height: "[1px]",
      backgroundColor: "whiteAlpha.10",
      marginTop: "12"
    })} style={{
      marginLeft: `-${horizontalPaddingPx}px`,
      marginRight: `-${horizontalPaddingPx}px`
    }} />;
    $[43] = heroAction;
    $[44] = horizontalPaddingPx;
    $[45] = nearbyValid;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const divider = t14;
  const maxHeight = !isMobilePane && !heroAction && nearbyValid ? 80 : undefined;
  const isBottomSection = bottomPlacement && isMobilePane;
  const bottomPaddingPx = isMobilePane ? isBottomSection ? 12 : horizontalPaddingPx : heroAction ? 36 : horizontalPaddingPx + (isCompactViewport ? compactTopOffset : topOffset);
  const topPaddingPx = isBottomSection ? 12 : isMobilePane ? horizontalPaddingPx : 0;
  const containerClassName = isBottomSection ? mobilePaneBottomSectionStyle : isMobilePane ? mobilePaneDrawerStyle : desktopDrawerStyle;
  const t15 = `${topPaddingPx}px ${horizontalPaddingPx}px ${bottomPaddingPx}px`;
  let t16;
  if ($[47] !== maxHeight) {
    t16 = maxHeight !== undefined && {
      maxHeight: `${maxHeight}px`
    };
    $[47] = maxHeight;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t15 || $[50] !== t16) {
    t17 = {
      padding: t15,
      ...t16
    };
    $[49] = t15;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  const containerInlineStyle = t17;
  let t18;
  if ($[52] !== containerClassName || $[53] !== containerInlineStyle || $[54] !== divider || $[55] !== heroRow || $[56] !== nearbyRow) {
    t18 = <div className={containerClassName} style={containerInlineStyle}>{heroRow}{divider}{nearbyRow}</div>;
    $[52] = containerClassName;
    $[53] = containerInlineStyle;
    $[54] = divider;
    $[55] = heroRow;
    $[56] = nearbyRow;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  return t18;
};
function _temp($) {
  return $.hero_action_cta_aria;
}
function _temp2($_0) {
  return $_0.back;
}
function _temp3($_1) {
  return $_1.pins_nearby;
}
function _temp4($_2) {
  return $_2.forward;
}